import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Grid, Hidden, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { type ClassesType, InfiniteLoaderList } from '@pbt/pbt-ui-components'

import {
  type PreviousMedicalRecord,
  useFetchPatientWithPmrDataOmnichannelQuery,
  useFetchPatientWithPmrDataQuery,
} from '~/api/graphql/generated/types'
import Needle from '~/components/common/Needle'
import FeatureToggle from '~/constants/featureToggle'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { TimelineItem } from '~/types'

import TimelineConversationMessageToggle from './TimelineConversationMessageToggle'
import TimelineFilters, { type TimelineFiltersProps } from './TimelineFilters'
import TimelinePrettyEmptyPanel from './TimelinePrettyEmptyPanel'
import TimelineTableRow from './TimelineTableRow'
import TimelineTimeframe from './TimelineTimeframe'

const flexFillMinZero = {
  flex: 1,
  minHeight: 0,
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      flex: 1,
      [theme.breakpoints.up('md')]: {
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
      },
      [theme.breakpoints.only('sm')]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
      [theme.breakpoints.only('md')]: {
        paddingRight: theme.spacing(2),
      },
      [theme.breakpoints.down('md')]: {
        paddingTop: theme.spacing(3),
        borderTop: theme.constants.tableBorder,
      },
    },
    timelineRoot: {
      height: '100%',
      position: 'relative',
    },
    timelineHeader: {
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(1),
      },
      [theme.breakpoints.up('lg')]: {
        paddingLeft: 0,
      },
    },
    cardsRoot: {
      flex: 1,
      paddingTop: 10,
      [theme.breakpoints.down('sm')]: {
        paddingTop: 0,
      },
    },
    contentRoot: {
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(1.5),
      },
    },
    mainContentColumn: {
      [theme.breakpoints.up('lg')]: {
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: theme.spacing(25),
      },
    },
    timelineContentWrapper: {
      [theme.breakpoints.up('lg')]: flexFillMinZero,
    },
    mainContentHorizontal: flexFillMinZero,
    legacyContentRow: flexFillMinZero,
    legacyMainContentCardsOnly: flexFillMinZero,
  }),
  { name: 'Timeline' },
)

export interface TimelineProps {
  className?: string
  classes?: ClassesType<typeof useStyles>
  clientId: string
  data: TimelineItem[]
  expanded?: boolean
  filterProps?: TimelineFiltersProps
  filtersAlwaysOnTop?: boolean
  initialItemParam: number
  isItemLoaded: (index: number) => boolean
  loadMoreItems: (startIndex: number, endIndex: number) => void
  patientId: string
  totalCount: number
  useWindowScroll?: boolean
}

const Timeline = ({
  className,
  classes: classesProp,
  clientId,
  data,
  expanded,
  filterProps = {},
  filtersAlwaysOnTop = false,
  isItemLoaded,
  loadMoreItems,
  patientId,
  totalCount,
  useWindowScroll = true,
  initialItemParam,
  ...rest
}: TimelineProps) => {
  const classes = useStyles({ classes: classesProp })
  const { pathname, search } = useLocation()
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const isRxTrackerPhase1Enabled = useSelector(
    getFeatureToggle(FeatureToggle.RX_TRACKER_PHASE_1),
  )
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )
  const isPmrConsentEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PMR_NEW_APPOINTMENT),
  )
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)

  // Fetch PMR data once at Timeline level to avoid multiple API calls from AppointmentCards
  const omnichannelPmr = useFetchPatientWithPmrDataOmnichannelQuery({
    variables: { patientId },
    skip: !isPmrConsentEnabled || !isOmnichannel,
  })
  const standardPmr = useFetchPatientWithPmrDataQuery({
    variables: { patientId },
    skip: !isPmrConsentEnabled || isOmnichannel,
  })
  const { data: pmrData } = isOmnichannel ? omnichannelPmr : standardPmr
  const previousMedicalRecord: PreviousMedicalRecord | null | undefined =
    pmrData?.patient?.previousMedicalRecord

  const isHorizontalLayout =
    useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg')) ||
    filtersAlwaysOnTop
  const useRxTrackerPhase1Layout =
    !isHorizontalLayout && isRxTrackerPhase1Enabled

  useEffect(() => {
    if (initialItemParam > 0) {
      return
    }

    requestAnimationFrame(() => {
      window.scroll({ top: 0, left: 0, behavior: 'smooth' })
    })
  }, [pathname, search])

  const timelineHeaderBlock = (
    <Grid
      container
      item
      alignItems="center"
      className={classes.timelineHeader}
      justifyContent="flex-start"
      sx={useRxTrackerPhase1Layout ? { paddingLeft: 0 } : undefined}
    >
      <TimelineTimeframe />
      {isSuppressAllSystemMessagesEnabled && (
        <TimelineConversationMessageToggle />
      )}
    </Grid>
  )

  const cardsBlock = (
    <Grid
      container
      item
      className={classes.cardsRoot}
      direction="column"
      justifyContent="flex-start"
      minHeight={totalCount ? 500 : 'initial'}
    >
      <Grid
        container
        item
        className={
          useRxTrackerPhase1Layout ? classes.timelineContentWrapper : undefined
        }
        direction="column"
        wrap="nowrap"
        xs={isHorizontalLayout ? 12 : useRxTrackerPhase1Layout ? 12 : 8}
      >
        <Hidden lgDown>
          <TimelinePrettyEmptyPanel
            clientId={clientId}
            itemData={data}
            patientId={patientId}
          />
        </Hidden>
        <Grid
          container
          item
          className={classes.timelineRoot}
          justifyContent="flex-start"
        >
          <Hidden smDown>{totalCount > 0 && <Needle twoWay />}</Hidden>
          <Grid item xs className={classes.contentRoot}>
            <InfiniteLoaderList
              disableInitialAutoScroll={Boolean(initialItemParam)}
              firstItemMargin={isMobile ? 24 : 0}
              focusedListItem={initialItemParam}
              isItemLoaded={isItemLoaded}
              itemCount={totalCount}
              itemData={data}
              itemSpacing={24}
              loadMoreItems={loadMoreItems}
              useWindowScroll={useWindowScroll}
              {...rest}
            >
              {(item, index) => (
                <TimelineTableRow
                  clientId={clientId}
                  expanded={expanded}
                  index={index}
                  isItemLoaded={isItemLoaded}
                  item={item}
                  patientId={patientId}
                  previousMedicalRecord={previousMedicalRecord}
                />
              )}
            </InfiniteLoaderList>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )

  const mainContent = (
    <Grid
      container
      item
      className={classNames(
        useRxTrackerPhase1Layout && classes.mainContentColumn,
        isHorizontalLayout && classes.mainContentHorizontal,
      )}
      direction="column"
      justifyContent="flex-start"
      lg={isHorizontalLayout ? undefined : useRxTrackerPhase1Layout ? true : 8}
      sx={
        useRxTrackerPhase1Layout
          ? { flex: 1, minWidth: 0, paddingLeft: 0 }
          : undefined
      }
      wrap="nowrap"
      xs={isHorizontalLayout ? 12 : true}
    >
      {timelineHeaderBlock}
      {cardsBlock}
    </Grid>
  )

  const mainContentCardsOnly = (
    <Grid
      container
      item
      xs
      className={classes.legacyMainContentCardsOnly}
      direction="column"
      justifyContent="flex-start"
      lg={8}
      wrap="nowrap"
    >
      {cardsBlock}
    </Grid>
  )

  return (
    <Grid
      container
      className={classNames(className, classes.root)}
      direction="column"
      wrap="nowrap"
    >
      {isHorizontalLayout && (
        <TimelineFilters {...filterProps} isHorizontalLayout />
      )}
      {isHorizontalLayout ? (
        mainContent
      ) : useRxTrackerPhase1Layout ? (
        <Grid container item spacing={0}>
          <TimelineFilters {...filterProps} />
          {mainContent}
        </Grid>
      ) : (
        <>
          {timelineHeaderBlock}
          <Grid
            container
            item
            className={classes.legacyContentRow}
            direction="row-reverse"
          >
            <TimelineFilters {...filterProps} />
            {mainContentCardsOnly}
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default Timeline
